/*
 * Copyright (c) CIRROM 2025.
 */

import React, { useEffect, useMemo, useState } from "react";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { blue, green, red } from "@mui/material/colors";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Rating from "@mui/material/Rating";
import Box from "@mui/material/Box";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SpellcheckOutlinedIcon from "@mui/icons-material/SpellcheckOutlined";

import classes from "../suggested-changes/css/SuggestedChangesRow.module.css";
import {
  CirromMonitorCRMComparison,
  EmailAndAddressSuggestion,
  acceptCirromMonitorChangeWithCompany,
  acceptCirromMonitorSuggestedChange,
  acceptCompanyAddressLink,
  approveAdHocMonitorChange,
  getEmailAndAddressSuggestions,
  rejectCirromMonitorSuggestedChanges,
} from "../../api/cirrommonitor";
import { AccountNameAutocomplete } from "../utils/datasources/AutoComplete";

interface MonitorComparisonTableProps {
  suggestedExplain: CirromMonitorCRMComparison;
  recordId: number;
  integratedSystemId: number;
  requestInProgress: boolean;
  setRequestInProgress: (value: boolean) => void;
  onSuccess: (id?) => void;
  displayAlert: (text: string, severity: string) => void;
}

export function MonitorComparisonTable(props: MonitorComparisonTableProps) {
  const {
    suggestedExplain,
    recordId,
    integratedSystemId,
    requestInProgress,
    setRequestInProgress,
    onSuccess,
    displayAlert,
  } = props;

  const [selectedCompany, setSelectedCompany] =
    useState<{ name: string; crm_account_id: string }>();
  const [companySuggestions, setCompanySuggestions] =
    useState<EmailAndAddressSuggestion | null>(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<string | "">("");
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [suggestionHighlight, setSuggestionHighlight] = useState(false);
  const [optimisticAccepted, setOptimisticAccepted] = useState<
    Map<string, string>
  >(new Map());
  const [addressLinked, setAddressLinked] = useState(false);

  useEffect(() => {
    setOptimisticAccepted(new Map());
    setAddressLinked(false);
  }, [recordId]);

  useEffect(() => {
    setCompanySuggestions(null);
    setSelectedAddressId("");
    setAddressLinked(false);
    setEditingField((prev) => (prev === "Email" ? null : prev));

    if (!selectedCompany || !integratedSystemId) {
      return;
    }
    let cancelled = false;
    setSuggestionsLoading(true);
    getEmailAndAddressSuggestions(
      integratedSystemId,
      recordId,
      selectedCompany.crm_account_id
    )
      .then((data) => {
        if (!cancelled) {
          setCompanySuggestions(data);
          setSelectedAddressId(
            data.addresses?.length === 1 ? data.addresses[0].address_id : ""
          );
        }
      })
      .catch(() => {
        if (!cancelled) setCompanySuggestions(null);
      })
      .finally(() => {
        if (!cancelled) setSuggestionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCompany, integratedSystemId, recordId]);

  useEffect(() => {
    const suggestedEmail = companySuggestions?.suggested_email;
    const crmEmail = suggestedExplain.crm_results.primary_email;
    if (suggestedEmail && suggestedEmail !== crmEmail) {
      setEditingField("Email");
      setEditValue(suggestedEmail);
      setSuggestionHighlight(true);
      const timer = setTimeout(() => setSuggestionHighlight(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [
    companySuggestions?.suggested_email,
    suggestedExplain.crm_results.primary_email,
  ]);

  useEffect(() => {
    if (
      companySuggestions?.addresses &&
      companySuggestions.addresses.length > 0
    ) {
      setSuggestionHighlight(true);
      const timer = setTimeout(() => setSuggestionHighlight(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [companySuggestions?.addresses]);

  const selectedAddress = companySuggestions?.addresses?.find(
    (a) => a.address_id === selectedAddressId
  );

  const getFirstValue = (dictObject, dictName) => {
    if (dictObject && dictObject[dictName] && dictObject[dictName].length > 0) {
      return dictObject[dictName][0];
    }
  };

  const isValueDifferent = (crmValue, monitorValue) => {
    return monitorValue && crmValue !== monitorValue;
  };

  const getAcceptedValue = (backendField?: string): string | undefined => {
    if (!backendField) return undefined;
    const server = suggestedExplain.accepted_results?.find(
      (ar) => ar.field === backendField
    );
    if (server) return server.new_value ?? "";
    return optimisticAccepted.get(backendField);
  };

  const getAcceptedProbability = (
    backendField?: string
  ): number | undefined => {
    if (!backendField) return undefined;
    return suggestedExplain.accepted_results?.find(
      (ar) => ar.field === backendField
    )?.probability;
  };

  const recordRows = useMemo(
    () => [
      [
        "Salutation",
        suggestedExplain.crm_results.salutation,
        false,
        undefined,
        "salutation",
      ],
      [
        "First Name",
        suggestedExplain.crm_results.first_name,
        getFirstValue(suggestedExplain.monitor_results, "first_name")?.value ??
          suggestedExplain.crm_results.first_name,
        getFirstValue(suggestedExplain.monitor_results, "first_name")?.id,
        "first_name",
      ],
      [
        "Middle Name",
        suggestedExplain.crm_results.middle_name,
        getFirstValue(suggestedExplain.monitor_results, "middle_name")?.value ??
          suggestedExplain.crm_results.middle_name,
        getFirstValue(suggestedExplain.monitor_results, "middle_name")?.id,
        "middle_name",
      ],
      [
        "Last Name",
        suggestedExplain.crm_results.last_name,
        getFirstValue(suggestedExplain.monitor_results, "last_name")?.value ??
          suggestedExplain.crm_results.last_name,
        getFirstValue(suggestedExplain.monitor_results, "last_name")?.id,
        "last_name",
      ],
      [
        "Goes By",
        suggestedExplain.crm_results.goes_by,
        getFirstValue(suggestedExplain.monitor_results, "goes_by")?.value ??
          suggestedExplain.crm_results.goes_by,
        getFirstValue(suggestedExplain.monitor_results, "goes_by")?.id,
        "goes_by",
      ],
      [
        "Suffix",
        suggestedExplain.crm_results.suffix,
        getFirstValue(suggestedExplain.monitor_results, "suffix")?.value ??
          suggestedExplain.crm_results.suffix,
        getFirstValue(suggestedExplain.monitor_results, "suffix")?.id,
        "suffix",
      ],
      [
        "Company",
        suggestedExplain.crm_results.company,
        getFirstValue(suggestedExplain.monitor_results, "company")?.value ??
          suggestedExplain.crm_results.company,
        getFirstValue(suggestedExplain.monitor_results, "company")?.id,
        "company",
      ],
      [
        "Job Title",
        suggestedExplain.crm_results.job_title,
        getFirstValue(suggestedExplain.monitor_results, "job_title")?.value ??
          suggestedExplain.crm_results.job_title,
        getFirstValue(suggestedExplain.monitor_results, "job_title")?.id,
        "job_title",
      ],
      [
        "Email",
        suggestedExplain.crm_results.primary_email,
        getFirstValue(suggestedExplain.monitor_results, "primary_email")
          ?.value ?? suggestedExplain.crm_results.primary_email,
        getFirstValue(suggestedExplain.monitor_results, "primary_email")?.id,
        "primary_email",
      ],
      [
        "City",
        suggestedExplain.crm_results.city,
        getFirstValue(suggestedExplain.monitor_results, "city")?.value ??
          suggestedExplain.crm_results.city,
        getFirstValue(suggestedExplain.monitor_results, "city")?.id,
        "city",
      ],
      [
        "Country",
        suggestedExplain.crm_results.country,
        getFirstValue(suggestedExplain.monitor_results, "country")?.value ??
          suggestedExplain.crm_results.country,
        getFirstValue(suggestedExplain.monitor_results, "country")?.id,
        "country",
      ],
    ],
    [suggestedExplain]
  );

  return (
    <TableContainer>
      <Table size={"small"}>
        <TableHead>
          <TableRow>
            <TableCell>
              <span />
            </TableCell>
            <TableCell align={"left"} sx={{ padding: "6px" }}>
              <Typography variant={"h5"} style={{ fontWeight: "bold" }}>
                CRM
              </Typography>
            </TableCell>
            <TableCell align={"left"} sx={{ padding: "6px" }}>
              <Typography variant={"h5"} style={{ fontWeight: "bold" }}>
                DATASOURCE: LinkedIN
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {recordRows.map((entry) => {
            const isDifferent = isValueDifferent(entry[1], entry[2]);
            const acceptedValue = getAcceptedValue(
              entry[4] as string | undefined
            );
            const accepted = acceptedValue !== undefined;
            const acceptedProbability = getAcceptedProbability(
              entry[4] as string | undefined
            );
            return (
              <React.Fragment key={entry[0]}>
                <TableRow>
                  <TableCell className={classes.tableCell}>
                    <div className={classes.tableCellHeaderContents}>
                      <Typography
                        variant={"h6"}
                        className={classes.tableCellHeaderText}
                      >
                        {entry[0]}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <div>
                      <Typography variant={"h6"}>{entry[1]}</Typography>
                    </div>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        {editingField === entry[0] && entry[2] !== false ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <TextField
                              fullWidth
                              variant="standard"
                              size="small"
                              margin="dense"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              autoFocus
                            />
                            <Tooltip title="Go back">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingField(null);
                                  setEditValue("");
                                }}
                              >
                                <ArrowBackIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Push manual overwrite to your CRM">
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  disabled={
                                    requestInProgress ||
                                    editValue === ((entry[1] as string) || "")
                                  }
                                  onClick={async () => {
                                    setRequestInProgress(true);
                                    try {
                                      await approveAdHocMonitorChange(
                                        recordId,
                                        integratedSystemId,
                                        entry[4],
                                        editValue,
                                        (entry[1] as string) || ""
                                      );
                                      setEditingField(null);
                                      setEditValue("");
                                      displayAlert(
                                        "Change successfully accepted.",
                                        "success"
                                      );
                                      onSuccess();
                                    } catch (err) {
                                      displayAlert(
                                        `Failed to accept ${entry[0]} change. Please try again.`,
                                        "error"
                                      );
                                    } finally {
                                      setRequestInProgress(false);
                                    }
                                  }}
                                >
                                  <SpellcheckOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </div>
                        ) : (
                          <>
                            <Typography
                              variant={"h6"}
                              style={{
                                color:
                                  isDifferent || accepted
                                    ? red[900]
                                    : "inherit",
                                fontWeight:
                                  isDifferent || accepted ? "bold" : "inherit",
                              }}
                            >
                              {accepted
                                ? acceptedValue || (entry[1] as string) || "—"
                                : entry[2] || ""}
                            </Typography>
                            {accepted && acceptedProbability != null && (
                              <Box className={classes.ratingBox}>
                                <Rating
                                  value={(5 * acceptedProbability) / 100}
                                  readOnly
                                  size={"small"}
                                  precision={0.25}
                                />
                              </Box>
                            )}
                          </>
                        )}
                        {entry[0] === "Company" &&
                          isDifferent &&
                          !accepted &&
                          !!integratedSystemId && (
                            <div style={{ marginTop: "8px" }}>
                              <AccountNameAutocomplete
                                integratedSystemId={integratedSystemId}
                                onSelectionChanged={setSelectedCompany}
                              />
                            </div>
                          )}
                        {entry[0] === "Email" && selectedCompany && !accepted && (
                          <div style={{ marginTop: "4px" }}>
                            {suggestionsLoading ? (
                              <CircularProgress size={16} />
                            ) : companySuggestions?.suggested_email ? (
                              <Typography
                                variant={"body2"}
                                style={{
                                  color: blue[700],
                                  fontStyle: "italic",
                                  backgroundColor: suggestionHighlight
                                    ? "#fff9c4"
                                    : "transparent",
                                  transition: "background-color 0.5s ease",
                                  padding: "2px 4px",
                                  borderRadius: "4px",
                                }}
                              >
                                Suggested (from company):{" "}
                                {companySuggestions.suggested_email}
                              </Typography>
                            ) : null}
                          </div>
                        )}
                        {(entry[0] === "City" || entry[0] === "Country") &&
                          selectedCompany &&
                          !accepted &&
                          companySuggestions?.addresses &&
                          companySuggestions.addresses.length > 0 && (
                            <div style={{ marginTop: "4px" }}>
                              {suggestionsLoading ? (
                                <CircularProgress size={16} />
                              ) : (
                                <>
                                  {selectedAddress && (
                                    <Typography
                                      variant={"body2"}
                                      style={{
                                        color: blue[700],
                                        fontStyle: "italic",
                                        backgroundColor: suggestionHighlight
                                          ? "#fff9c4"
                                          : "transparent",
                                        transition:
                                          "background-color 0.5s ease",
                                        padding: "2px 4px",
                                        borderRadius: "4px",
                                      }}
                                    >
                                      Suggested (from company):{" "}
                                      {entry[0] === "City"
                                        ? selectedAddress.city
                                        : selectedAddress.country}
                                    </Typography>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "end",
                        }}
                      >
                        {accepted ? (
                          <Typography
                            variant={"h6"}
                            style={{ color: green[900], fontWeight: "bold" }}
                          >
                            Approved
                          </Typography>
                        ) : (
                          <>
                            {!(
                              entry[0] === "Company" &&
                              isDifferent &&
                              integratedSystemId
                            ) &&
                              entry[2] !== false &&
                              editingField !== entry[0] && (
                                <div>
                                  <Tooltip title="Edit this value through Cirrom">
                                    <IconButton
                                      size="small"
                                      color={"info"}
                                      disabled={requestInProgress}
                                      onClick={() => {
                                        setEditingField(entry[0] as string);
                                        setEditValue(
                                          (entry[2] as string) ||
                                            (entry[1] as string) ||
                                            ""
                                        );
                                      }}
                                    >
                                      <DriveFileRenameOutlineOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              )}

                            {isDifferent && editingField !== entry[0] && (
                              <div>
                                <IconButton
                                  size="small"
                                  style={{ color: green[500] }}
                                  disabled={
                                    requestInProgress ||
                                    (entry[0] === "Company" &&
                                      !selectedCompany) ||
                                    (entry[0] !== "Company" && !entry[3])
                                  }
                                  onClick={async () => {
                                    if (
                                      entry[0] === "Company" &&
                                      selectedCompany &&
                                      suggestedExplain?.pending_results
                                    ) {
                                      setRequestInProgress(true);
                                      try {
                                        await acceptCirromMonitorChangeWithCompany(
                                          recordId,
                                          selectedCompany.name,
                                          selectedCompany.crm_account_id,
                                          integratedSystemId
                                        );
                                        setOptimisticAccepted((m) =>
                                          new Map(m).set(
                                            "company",
                                            selectedCompany.name
                                          )
                                        );
                                        onSuccess();
                                      } catch (err) {
                                        displayAlert(
                                          "Failed to accept company change. Please try again.",
                                          "error"
                                        );
                                      } finally {
                                        setRequestInProgress(false);
                                      }
                                    } else if (entry[3]) {
                                      setRequestInProgress(true);
                                      try {
                                        await acceptCirromMonitorSuggestedChange(
                                          [
                                            {
                                              record_id: recordId,
                                              integrated_system_id:
                                                integratedSystemId,
                                              suggestion_id: entry[3] as number,
                                            },
                                          ]
                                        );
                                        if (entry[4]) {
                                          setOptimisticAccepted((m) =>
                                            new Map(m).set(
                                              entry[4] as string,
                                              (entry[2] as string) || ""
                                            )
                                          );
                                        }
                                        onSuccess();
                                      } catch (err) {
                                        displayAlert(
                                          `Failed to accept ${entry[0]} change. Please try again.`,
                                          "error"
                                        );
                                      } finally {
                                        setRequestInProgress(false);
                                      }
                                    }
                                  }}
                                >
                                  <CheckCircleOutlinedIcon fontSize="small" />
                                </IconButton>

                                <IconButton
                                  size="small"
                                  style={{ color: red[500] }}
                                  disabled={requestInProgress || !entry[3]}
                                  onClick={async () => {
                                    if (entry[3]) {
                                      setRequestInProgress(true);
                                      try {
                                        await rejectCirromMonitorSuggestedChanges(
                                          [entry[3] as number]
                                        );

                                        onSuccess();
                                      } catch (err) {
                                        displayAlert(
                                          `Failed to reject ${entry[0]} change. Please try again.`,
                                          "error"
                                        );
                                      } finally {
                                        setRequestInProgress(false);
                                      }
                                    }
                                  }}
                                >
                                  <CancelOutlinedIcon fontSize="small" />
                                </IconButton>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
                {entry[0] === "Company" &&
                  companySuggestions?.addresses &&
                  companySuggestions.addresses.length > 0 && (
                    <TableRow>
                      <TableCell className={classes.tableCell}>
                        <div className={classes.tableCellHeaderContents}>
                          <Typography
                            variant={"h6"}
                            className={classes.tableCellHeaderText}
                          >
                            Address Link
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell className={classes.tableCell} />
                      <TableCell className={classes.tableCell}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            {!addressLinked && (
                              <Select
                                size="small"
                                value={selectedAddressId}
                                onChange={(e) =>
                                  setSelectedAddressId(e.target.value)
                                }
                                displayEmpty
                                sx={{
                                  minWidth: 200,
                                  marginBottom: "4px",
                                }}
                              >
                                <MenuItem value="" disabled>
                                  Select an address
                                </MenuItem>
                                {companySuggestions.addresses.map((addr) => (
                                  <MenuItem
                                    key={addr.address_id}
                                    value={addr.address_id}
                                  >
                                    {[
                                      addr.street,
                                      addr.city,
                                      addr.stateorprovince,
                                      addr.country,
                                      addr.zipcode,
                                    ]
                                      .filter(Boolean)
                                      .join(", ")}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "end",
                            }}
                          >
                            {addressLinked ? (
                              <Typography
                                variant={"h6"}
                                style={{
                                  color: green[900],
                                  fontWeight: "bold",
                                }}
                              >
                                Linked
                              </Typography>
                            ) : (
                              <Tooltip title="Link this address to the contact">
                                <span>
                                  <IconButton
                                    size="small"
                                    style={{ color: green[500] }}
                                    disabled={
                                      requestInProgress || !selectedAddressId
                                    }
                                    onClick={async () => {
                                      setRequestInProgress(true);
                                      try {
                                        await acceptCompanyAddressLink(
                                          recordId,
                                          integratedSystemId,
                                          selectedAddressId
                                        );
                                        setAddressLinked(true);
                                        displayAlert(
                                          "Address link successfully accepted.",
                                          "success"
                                        );
                                        onSuccess();
                                      } catch (err) {
                                        displayAlert(
                                          "Failed to link address. Please try again.",
                                          "error"
                                        );
                                      } finally {
                                        setRequestInProgress(false);
                                      }
                                    }}
                                  >
                                    <CheckCircleOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
